import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { SlEnvolope, SlLock, SlLogout, SlSettings, SlUser } from 'react-icons/sl'
import { Button } from '@/components/orders/Button'
import { publicConfig } from '@/config/publicConfig'
import { DEFAULT_TRADE_MARGIN_TYPE } from '@/constants/marginTypes'
import useSignOutAndClearStore from '@/hooks/useSignOutAndClearStore'
import { useGetMarketMakerSelf, useGetUserSelf, userRole } from '@/hooks/v1'
import {
  isUserAdmin,
  isUserAffiliateManager,
  isUserCompetitionManager,
  isUserIntegrator,
} from '@/types/Role'
import { getApiKey, useApiKey } from '@/utils/apiKeyAuth'
import { copyToken } from '@/utils/tokenUtils'

interface MobileMenuProps {
  setIsMobileMenuOpen: (isActive: boolean) => void
}

export const MobileMenu = ({ setIsMobileMenuOpen }: MobileMenuProps) => {
  'use no memo'

  const { t } = useTranslation()
  const { data: userSelf } = useGetUserSelf()
  const { data: marketMakerSelf } = useGetMarketMakerSelf({
    query: { retry: false, staleTime: 5 * 60_000 },
  })
  const apiKey = useApiKey()
  const user = userSelf?.data
  const canAccessAdministration =
    isUserAdmin(user) ||
    isUserIntegrator(user) ||
    isUserCompetitionManager(user) ||
    isUserAffiliateManager(user)
  const isMarketMaker = Boolean(marketMakerSelf?.data) || Boolean(isUserAdmin(user))
  const canAccessStrategyBuilder =
    user?.roles?.some((role) => role === userRole.ADMIN || role === userRole.TRADER) &&
    Boolean(apiKey) &&
    Boolean(publicConfig.strategy.apiUrl.trim())
  const signOutAndClearStore = useSignOutAndClearStore()
  const navigate = useNavigate()
  const handleSignOut = async () => {
    const hadApiKey = Boolean(getApiKey())
    await signOutAndClearStore()
      .catch(() => {
        return
      })
      .finally(() => {
        setIsMobileMenuOpen(false)
      })

    await navigate({
      to: hadApiKey ? '/auth/session-closed' : '/auth/sign-in',
      replace: true,
    })
  }

  return (
    <nav
      id="mobile-navigation"
      aria-label="Mobile navigation"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setIsMobileMenuOpen(false)
          document.getElementById('mobile-menu-toggle')?.focus()
        }
      }}
      className="shrink-0 border-b border-white/10 px-4 pb-3"
    >
      <div className="divide-y divide-white/10 py-1">
        <Link
          to="/trade"
          search={{ marginType: DEFAULT_TRADE_MARGIN_TYPE }}
          from="/"
          className="nav-link"
          onClick={() => {
            setIsMobileMenuOpen(false)
          }}
        >
          <div className="menu nav-item relative flex items-center text-white text-sm min-h-11 py-2">
            <span className="px-1">{t('Trade')}</span>
          </div>
        </Link>
        <Link
          to="/portfolio"
          from="/"
          className="nav-link"
          onClick={() => {
            setIsMobileMenuOpen(false)
          }}
        >
          <div className="menu nav-item relative flex items-center text-white text-sm min-h-11 py-2">
            <span className="px-1">{t('Portfolio')}</span>
          </div>
        </Link>
        <Link
          to="/leaderboards"
          from="/"
          className="nav-link"
          onClick={() => {
            setIsMobileMenuOpen(false)
          }}
        >
          <div className="menu nav-item relative flex items-center text-white text-sm min-h-11 py-2">
            <span className="px-1">{t('Leaderboards')}</span>
          </div>
        </Link>
        <Link
          to="/explorer"
          from="/"
          className="nav-link"
          onClick={() => {
            setIsMobileMenuOpen(false)
          }}
        >
          <div className="menu nav-item relative flex items-center text-white text-sm min-h-11 py-2">
            <span className="px-1">{t('Explorer')}</span>
          </div>
        </Link>
        <Link
          to="/trading-strategies"
          from="/"
          className="nav-link"
          onClick={() => {
            setIsMobileMenuOpen(false)
          }}
        >
          <div className="menu nav-item relative flex items-center text-white text-sm min-h-11 py-2">
            <span className="px-1">{t('Trading Strategies')}</span>
          </div>
        </Link>
        {canAccessStrategyBuilder && (
          <Link
            to="/strategy-builder"
            from="/"
            className="nav-link"
            onClick={() => {
              setIsMobileMenuOpen(false)
            }}
          >
            <div className="menu nav-item relative flex items-center text-white text-sm min-h-11 py-2">
              <span className="px-1">{t('Strategy Builder')}</span>
            </div>
          </Link>
        )}
        {isMarketMaker && (
          <Link
            to="/market-maker"
            from="/"
            className="nav-link"
            onClick={() => {
              setIsMobileMenuOpen(false)
            }}
          >
            <div className="menu nav-item relative flex items-center text-white text-sm min-h-11 py-2">
              <span className="px-1">{t('Market Making')}</span>
            </div>
          </Link>
        )}
        {canAccessAdministration ? (
          <Link
            to="/administration"
            from="/"
            className="nav-link"
            onClick={() => {
              setIsMobileMenuOpen(false)
            }}
          >
            <div className="menu nav-item relative flex items-center text-white text-sm min-h-11 py-2">
              <span className="px-1">{t('Admin')}</span>
            </div>
          </Link>
        ) : null}
      </div>

      <details className="border-t border-white/10 pt-2 text-sm text-white-dark">
        <summary className="flex min-h-11 cursor-pointer items-center">Account & settings</summary>
        <div className="pb-3 font-semibold">
          <div>
            <div className="flex items-center">
              <svg
                className="rounded-md w-10 h-10 p-2 bg-gray-100 dark:bg-gray-700"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
              <div className="ltr:pl-4 rtl:pr-4 truncate">
                <h4 className="text-base">
                  {user?.first_name ?? 'User'}
                  {user?.last_name ?? 'User'}
                  <span className="text-xs bg-success-light rounded text-success px-1 ltr:ml-2 rtl:ml-2">
                    Pro
                  </span>
                </h4>
                <button
                  type="button"
                  className="text-black/60 hover:text-primary dark:text-dark-light/60 dark:hover:text-white"
                >
                  {user?.email}
                </button>
              </div>
            </div>
          </div>

          <div className="w-full flex justify-between mt-8">
            <div className="w-full">
              <Link
                onClick={() => setIsMobileMenuOpen(false)}
                to="/user/profile"
                className="flex items-center dark:hover:text-white"
              >
                <SlUser size={24} className="mr-2 text-gray-400" />
                Profile
              </Link>
            </div>
            <div className="w-full">
              <Link
                onClick={() => setIsMobileMenuOpen(false)}
                to="/user/settings"
                className="flex items-center dark:hover:text-white"
              >
                <SlSettings size={24} className="mr-2 text-gray-400" />
                Account Settings
              </Link>
            </div>
          </div>

          <div className="w-full flex justify-between mt-4">
            <div className="w-full">
              <button
                type="button"
                className="flex items-center dark:hover:text-white"
                onClick={(event) => event.preventDefault()}
              >
                <SlEnvolope size={24} className="mr-2 text-gray-400" />
                Inbox
              </button>
            </div>
            <div className="w-full">
              <button
                type="button"
                className="flex items-center dark:hover:text-white"
                onClick={(event) => event.preventDefault()}
              >
                <SlLock size={24} className="mr-2 text-gray-400" />
                Lock Screen
              </button>
            </div>
          </div>

          <Link
            to="/user/affiliates"
            className="mt-4 flex items-center dark:hover:text-white"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <SlUser size={24} className="mr-2 text-gray-400" />
            Affiliates panel
          </Link>

          <div className="w-full mt-4">
            <div className="border-t border-white-light dark:border-white-light/10">
              <button
                type="button"
                className="flex w-full items-center text-danger"
                onClick={handleSignOut}
              >
                <SlLogout size={24} className="pr-0.5 mr-2 text-danger" />
                Sign Out
              </button>
            </div>
          </div>

          <div className="p-2 mt-8">
            <Button
              className="w-full btn bg-gradient-to-r from-secondary to-primary hover:from-primary hover:to-secondary text-black border-0 uppercase shadow-[0_10px_20px_-10px_rgba(67,97,238,0.44)]"
              onClick={copyToken}
            >
              Copy token
            </Button>
          </div>
        </div>
      </details>
    </nav>
  )
}
